import React, { useState } from "react";
import { Link } from "react-router-dom";
import { DoorOpen, MessageSquare, MessageCircle, ArrowRight, Clock } from "lucide-react";
import { Card, EmptyState } from "../common/Primitives";
import { formatDateTime } from "../../lib/datetime";

const TABS = [
  { key: "rooms", label: "Deal Rooms", icon: DoorOpen },
  { key: "queries", label: "Queries", icon: MessageSquare },
  { key: "messages", label: "Messages", icon: MessageCircle },
];

/**
 * InMotionSection
 * ---------------
 * Combined view of an investor's active pipeline:
 *   - Deal rooms (not Dropped)
 *   - Open queries (awaiting responses)
 *   - Recent message threads
 * Tabs to switch view; counts shown per tab.
 */
export default function InMotionSection({ data }) {
  const [active, setActive] = useState("rooms");
  const rooms = data?.deal_rooms || [];
  const queries = data?.open_queries || [];
  const threads = data?.recent_threads || [];

  const counts = { rooms: rooms.length, queries: queries.length, messages: threads.length };
  const totalCount = counts.rooms + counts.queries + counts.messages;

  return (
    <Card testId="section-in-motion">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">In Motion</div>
          <div className="font-heading text-ink text-lg mt-1">
            Active conversations
            {totalCount > 0 && (
              <span className="ml-2 text-sm text-ink-muted font-mono" data-testid="in-motion-total-count">({totalCount})</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-navy/10 mb-4" data-testid="in-motion-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              className={`px-3 py-2 text-xs font-heading uppercase tracking-widest border-b-2 transition-colors inline-flex items-center gap-1.5 ${
                isActive
                  ? "border-emerald-600 text-emerald-700"
                  : "border-transparent text-ink-muted hover:text-ink"
              }`}
              data-testid={`in-motion-tab-${t.key}`}
            >
              <Icon size={12} />
              {t.label}
              <span className={`ml-1 px-1.5 rounded-full text-[10px] font-mono ${
                isActive ? "bg-emerald-100 text-emerald-800" : "bg-navy/[0.06] text-ink-muted"
              }`}>
                {counts[t.key]}
              </span>
            </button>
          );
        })}
      </div>

      {active === "rooms" && (
        <div data-testid="in-motion-rooms">
          {rooms.length === 0 ? (
            <EmptyState title="No active deal rooms" hint="Once a startup accepts your interest, a deal room opens here." />
          ) : (
            <div className="space-y-2">
              {rooms.slice(0, 5).map((r) => (
                <Link
                  key={r.deal_room_id}
                  to={`/deal-room/${r.deal_room_id}`}
                  className="block border border-white/10 p-3 rounded hover:border-emerald-300 transition-colors"
                  data-testid={`in-motion-room-${r.deal_room_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-ink text-sm truncate">{r.startup_name}</div>
                      <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mt-0.5">
                        {r.sector || "—"} · {r.stage}
                      </div>
                    </div>
                    <ArrowRight size={14} className="text-emerald-600 flex-shrink-0 ml-2" />
                  </div>
                  {r.updated_at && (
                    <div className="text-[10px] font-mono text-ink-faint mt-1 flex items-center gap-1">
                      <Clock size={10} /> {formatDateTime(r.updated_at)}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {active === "queries" && (
        <div data-testid="in-motion-queries">
          {queries.length === 0 ? (
            <EmptyState title="No open queries" hint="Send structured queries to startups to fast-track diligence." />
          ) : (
            <div className="space-y-2">
              {queries.slice(0, 5).map((q) => (
                <Link
                  key={q.query_id}
                  to="/investor/queries"
                  className="block border border-white/10 p-3 rounded hover:border-emerald-300 transition-colors"
                  data-testid={`in-motion-query-${q.query_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-ink text-sm truncate">{q.startup_name}</div>
                      <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mt-0.5">
                        {q.num_questions} question{q.num_questions === 1 ? "" : "s"} · {q.status}
                      </div>
                    </div>
                    <ArrowRight size={14} className="text-emerald-600 flex-shrink-0 ml-2" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {active === "messages" && (
        <div data-testid="in-motion-messages">
          {threads.length === 0 ? (
            <EmptyState title="No conversations yet" hint="Direct-message with startups or your VenturaZ manager anytime." />
          ) : (
            <div className="space-y-2">
              {threads.slice(0, 5).map((t) => (
                <Link
                  key={t.thread_key}
                  to="/messages"
                  className="block border border-white/10 p-3 rounded hover:border-emerald-300 transition-colors"
                  data-testid={`in-motion-thread-${t.thread_key}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-heading text-ink text-sm truncate">
                      {t.counterparty_name}
                      {t.counterparty_role && (
                        <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-ink-faint">
                          {t.counterparty_role}
                        </span>
                      )}
                    </div>
                    <ArrowRight size={14} className="text-emerald-600 flex-shrink-0 ml-2" />
                  </div>
                  <div className="text-xs text-ink-muted truncate">
                    {t.from_me && <span className="text-ink-faint">You: </span>}
                    {t.last_message || "…"}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
